import { fmt, fmtNum, pct } from "./utils";
import _ from "lodash";

function loadScript(src) {
  return new Promise((resolve, reject) => {
    if (document.querySelector('script[src="' + src + '"]')) { resolve(); return; }
    const s = document.createElement("script");
    s.src = src;
    s.onload = resolve;
    s.onerror = reject;
    document.head.appendChild(s);
  });
}

export async function generateOnePager({ filteredData, allData, eventOverrides, orgSettings, lineItemActuals = {}, qualificationFields = [] }) {
  await loadScript("https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js");
  const { jsPDF } = window.jspdf;
  const pdf = new jsPDF({ orientation: "landscape", unit: "pt", format: "letter" });
  const pw = 792;
  const ph = 612;
  const m = { l: 40, r: 40, t: 40, b: 30 };
  const cw = pw - m.l - m.r;

  const onyx = [10, 23, 32];
  const onyxLt = [15, 34, 48];
  const cyan = [34, 211, 238];
  const orange = [34, 211, 238];
  const white = [255, 255, 255];
  const fog = [255, 255, 255];
  const stone = [83, 92, 98];
  const green = [34, 211, 238];
  const gold = [34, 211, 238];
  const teal = [34, 211, 238];
  const blueLt = [34, 211, 238];

  const settings = orgSettings || {};
  const kpiLabels = settings.kpi_labels || {};
  const teams = settings.teams || [];
  const rankings = settings.rankings || [];
  const companyName = settings.branding?.company_name || "EXIQ";

  const data = filteredData && filteredData.length > 0 ? filteredData : allData;
  const total = data.length;
  const rankingField = (qualificationFields || []).find(f => f.is_ranking_field);
  const hotLabel = rankingField?.options?.[0]?.short_label || rankingField?.options?.[0]?.label || "Hot";
  const warmLabel = rankingField?.options?.[1]?.short_label || rankingField?.options?.[1]?.label || "Warm";
  const coldLabel = rankingField?.options?.[2]?.short_label || rankingField?.options?.[2]?.label || "Cold";
  const getRankVal = (r) => {
    if (!rankingField) return r["Ranking"] || "";
    if (r.custom_data && r.custom_data[rankingField.id] !== undefined) return String(r.custom_data[rankingField.id]);
    if (rankingField.csv_column_match && r[rankingField.csv_column_match]) return String(r[rankingField.csv_column_match]);
    return r[rankingField.label] || r["Ranking"] || "";
  };
  const matchRankOpt = (val, opt) => {
    const v = String(val || "").trim().toLowerCase();
    return v === (opt.value||"").toLowerCase() || v === (opt.label||"").toLowerCase() || v === (opt.short_label||"").toLowerCase();
  };
  const hot = rankingField?.options?.[0] ? data.filter(r => matchRankOpt(getRankVal(r), rankingField.options[0])).length : data.filter(r => getRankVal(r).toLowerCase().includes("hot")).length;
  const warm = rankingField?.options?.[1] ? data.filter(r => matchRankOpt(getRankVal(r), rankingField.options[1])).length : data.filter(r => getRankVal(r).toLowerCase().includes("warm")).length;
  const cold = rankingField?.options?.[2] ? data.filter(r => matchRankOpt(getRankVal(r), rankingField.options[2])).length : data.filter(r => getRankVal(r).toLowerCase().includes("cold")).length;
  const demos = data.filter(r => (r["Received Demo"] || "").toLowerCase().trim() === "yes").length;
  const events = [...new Set(data.map(r => r["Primary Campaign Source"]).filter(Boolean))];

  const overridePipeline = events.reduce((s, e) => s + (eventOverrides[e]?.influenced_pipeline || 0), 0);
  const overrideNetNew = events.reduce((s, e) => s + (eventOverrides[e]?.net_new_opportunities || 0), 0);
  const totalCost = events.reduce((s, e) => s + (lineItemActuals[e] || eventOverrides[e]?.total_cost || 0), 0);
  const totalMeetings = events.reduce((s, e) => s + (eventOverrides[e]?.meetings || 0), 0);

  const byEvent = _(data).groupBy("Primary Campaign Source").map((rows, name) => ({
    name,
    leads: rows.length,
    hot: rankingField?.options?.[0] ? rows.filter(r => matchRankOpt(getRankVal(r), rankingField.options[0])).length : rows.filter(r => getRankVal(r).toLowerCase().includes("hot")).length,
    warm: rankingField?.options?.[1] ? rows.filter(r => matchRankOpt(getRankVal(r), rankingField.options[1])).length : rows.filter(r => getRankVal(r).toLowerCase().includes("warm")).length,
    cold: rankingField?.options?.[2] ? rows.filter(r => matchRankOpt(getRankVal(r), rankingField.options[2])).length : rows.filter(r => getRankVal(r).toLowerCase().includes("cold")).length,
    demos: rows.filter(r => (r["Received Demo"] || "").toLowerCase().trim() === "yes").length,
  })).value().sort((a, b) => b.leads - a.leads);

  Object.keys(eventOverrides).forEach(name => {
    if (!byEvent.find(e => e.name === name)) {
      byEvent.push({ name, leads: 0, hot: 0, warm: 0, cold: 0, demos: 0 });
    }
  });

  pdf.setFillColor(...onyx);
  pdf.rect(0, 0, pw, ph, "F");

  pdf.setFillColor(...cyan);
  pdf.rect(0, 0, pw, 4, "F");

  let y = m.t + 8;
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(18);
  pdf.setTextColor(...white);
  pdf.text(companyName + " \u2014 Exhibitor Intelligence", m.l, y);
  pdf.setFontSize(9);
  pdf.setTextColor(...fog);
  pdf.text(new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }), pw - m.r, y, { align: "right" });

  y += 10;
  pdf.setDrawColor(...cyan);
  pdf.setLineWidth(1.5);
  pdf.line(m.l, y, pw - m.r, y);

  y += 20;
  const leadW = cw * 0.32;
  const otherW = (cw - leadW - 12) / 4;

  pdf.setFillColor(...onyxLt);
  pdf.roundedRect(m.l, y, leadW, 62, 3, 3, "F");
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(7);
  pdf.setTextColor(...stone);
  pdf.text((kpiLabels.leads || "TOTAL LEADS").toUpperCase(), m.l + 10, y + 14);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(22);
  pdf.setTextColor(...white);
  pdf.text(total.toLocaleString(), m.l + 10, y + 36);

  const barY = y + 44;
  const barH = 5;
  const barW = leadW - 20;
  const ranked = hot + warm + cold || 1;
  const hotPct = hot / ranked;
  const warmPct = warm / ranked;
  const coldPct = cold / ranked;
  pdf.setFillColor(...orange);
  pdf.roundedRect(m.l + 10, barY, barW * hotPct, barH, 2, 2, "F");
  pdf.setFillColor(...gold);
  pdf.rect(m.l + 10 + barW * hotPct, barY, barW * warmPct, barH, "F");
  pdf.setFillColor(...blueLt);
  pdf.roundedRect(m.l + 10 + barW * (hotPct + warmPct), barY, barW * coldPct, barH, 0, 0, "F");

  pdf.setFontSize(7);
  pdf.setFont("helvetica", "bold");
  let lx = m.l + 10;
  [
    [hot, rankings[0]?.label || "Hot", orange],
    [warm, rankings[1]?.label || "Warm", gold],
    [cold, rankings[2]?.label || "Cold", blueLt],
  ].forEach(([v, label, clr]) => {
    pdf.setTextColor(...clr);
    pdf.text(String(v), lx, y + 58);
    const vW = pdf.getTextWidth(String(v));
    pdf.setTextColor(...stone);
    pdf.setFont("helvetica", "normal");
    pdf.text(" " + label, lx + vW, y + 58);
    lx += vW + pdf.getTextWidth(" " + label) + 10;
    pdf.setFont("helvetica", "bold");
  });

  const kpis = [
    { label: (kpiLabels.influenced_pipeline || "INFLUENCED PIPELINE").toUpperCase(), value: overridePipeline > 0 ? fmt(overridePipeline) : "\u2014", color: green },
    { label: (kpiLabels.net_new || "NET-NEW OPP").toUpperCase(), value: overrideNetNew > 0 ? fmt(overrideNetNew) : "\u2014", color: gold },
    { label: "DEMOS GIVEN", value: demos.toLocaleString(), color: orange },
    { label: (kpiLabels.meetings || "MEETINGS").toUpperCase(), value: totalMeetings > 0 ? totalMeetings.toLocaleString() : "\u2014", color: teal },
  ];
  kpis.forEach((k, i) => {
    const x = m.l + leadW + 12 + i * (otherW + 4);
    pdf.setFillColor(...onyxLt);
    pdf.roundedRect(x, y, otherW - 4, 62, 3, 3, "F");
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(7);
    pdf.setTextColor(...stone);
    pdf.text(k.label, x + (otherW - 4) / 2, y + 14, { align: "center" });
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(16);
    pdf.setTextColor(...k.color);
    pdf.text(k.value, x + (otherW - 4) / 2, y + 38, { align: "center" });
  });

  y += 78;
  const teamData = teams.map(team => {
    const teamEvents = byEvent.filter(e => (eventOverrides[e.name] || {}).segment === team.name);
    const leads = _.sumBy(teamEvents, "leads");
    const hotCount = _.sumBy(teamEvents, "hot");
    const demosCount = _.sumBy(teamEvents, "demos");
    const evNames = teamEvents.map(e => e.name);
    const pipeline = evNames.reduce((s, n) => s + (eventOverrides[n]?.influenced_pipeline || 0), 0);
    const netNew = evNames.reduce((s, n) => s + (eventOverrides[n]?.net_new_opportunities || 0), 0);
    const meetings = evNames.reduce((s, n) => s + (eventOverrides[n]?.meetings || 0), 0);
    const cost = evNames.reduce((s, n) => s + (lineItemActuals[n] || eventOverrides[n]?.total_cost || 0), 0);
    const cpl = cost > 0 && leads > 0 ? cost / leads : 0;
    return { team: team.name, events: teamEvents.length, leads, hot: hotCount, demos: demosCount, pipeline, netNew, meetings, cost, cpl };
  }).filter(d => d.events > 0);

  if (teamData.length > 0) {
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(10);
    pdf.setTextColor(...fog);
    pdf.text("ACCOUNT TEAM PERFORMANCE", m.l, y);
    y += 14;

    const cols = ["Account Team", "Events", "Leads", hotLabel, "Demos", "Demo %", "Meetings", "Cost", kpiLabels.influenced_pipeline || "Influenced Pipeline", kpiLabels.net_new || "Net-New Opp", "Cost/Lead", "ROI"];
    const colW = [95, 38, 38, 35, 38, 43, 43, 52, 80, 68, 55, 45];
    pdf.setFillColor(...onyxLt);
    pdf.rect(m.l, y, cw, 16, "F");
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(7);
    pdf.setTextColor(...stone);
    let cx = m.l + 6;
    cols.forEach((c, i) => { pdf.text(c.toUpperCase(), cx, y + 11); cx += colW[i]; });
    y += 16;

    teamData.forEach(td => {
      if (y > ph - 100) return;
      const roi = td.cost > 0 && td.pipeline > 0 ? (td.pipeline / td.cost).toFixed(1) + "x" : "\u2014";
      const vals = [td.team, td.events, td.leads, td.hot, td.demos, td.leads > 0 ? (td.demos / td.leads * 100).toFixed(1) + "%" : "\u2014", td.meetings > 0 ? td.meetings : "\u2014", td.cost > 0 ? fmt(td.cost) : "\u2014", td.pipeline > 0 ? fmt(td.pipeline) : "\u2014", td.netNew > 0 ? fmt(td.netNew) : "\u2014", td.cpl > 0 ? fmt(td.cpl) : "\u2014", roi];
      cx = m.l + 6;
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(8);
      pdf.setTextColor(...fog);
      vals.forEach((v, i) => { pdf.text(String(v), cx, y + 11); cx += colW[i]; });
      pdf.setDrawColor(...stone);
      pdf.setLineWidth(0.3);
      pdf.line(m.l, y + 15, m.l + cw, y + 15);
      y += 16;
    });
    y += 22;
  }

  // ── Budget Summary ──
  const totalBudget = Object.values(lineItemActuals).reduce((s, v) => s + v, 0);
  const budgetEvents = Object.keys(lineItemActuals);
  if (totalBudget > 0 && y < ph - 120) {
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(10);
    pdf.setTextColor(...fog);
    pdf.text("BUDGET SUMMARY", m.l, y);
    y += 14;
    const bCols = [["Total Actuals", fmt(totalBudget), cyan], ["Events Tracked", budgetEvents.length.toString(), fog], ["Pipeline ROI", totalBudget > 0 && overridePipeline > 0 ? (overridePipeline/totalBudget).toFixed(1)+"x" : "—", gold], ["Cost/Lead", totalBudget > 0 && total > 0 ? fmt(Math.round(totalBudget/total)) : "—", teal]];
    const bw = cw / bCols.length;
    bCols.forEach(([label, val, color], i) => {
      const bx = m.l + i * bw;
      pdf.setFillColor(...onyxLt);
      pdf.roundedRect(bx, y, bw - 6, 44, 3, 3, "F");
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(7);
      pdf.setTextColor(...stone);
      pdf.text(label.toUpperCase(), bx + (bw-6)/2, y + 13, { align: "center" });
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(14);
      pdf.setTextColor(...color);
      pdf.text(val, bx + (bw-6)/2, y + 33, { align: "center" });
    });
    y += 58;
  }

  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(10);
  pdf.setTextColor(...fog);
  pdf.text("EVENT PERFORMANCE", m.l, y);
  y += 14;

  const evCols = ["Event", "Team", "Leads", hotLabel, warmLabel, coldLabel, "Demos", "Demo %", "Meetings", "Cost", kpiLabels.influenced_pipeline || "Pipeline", "ROI"];
  const evColW = [125, 80, 33, 35, 35, 35, 33, 43, 43, 58, 85, 47];
  pdf.setFillColor(...onyxLt);
  pdf.rect(m.l, y, cw, 16, "F");
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(7);
  pdf.setTextColor(...stone);
  let ex = m.l + 6;
  evCols.forEach((c, i) => { pdf.text(c.toUpperCase(), ex, y + 11); ex += evColW[i]; });
  y += 16;

  byEvent.forEach(ev => {
    if (y > ph - m.b - 20) return;
    const ov = eventOverrides[ev.name] || {};
    const evName = ev.name.length > 28 ? ev.name.slice(0, 26) + "..." : ev.name;
    const seg = ov.segment || "\u2014";
    const evCost = lineItemActuals[ev.name] || ov.total_cost || 0;
    const evRoi = evCost > 0 && ov.influenced_pipeline > 0 ? (ov.influenced_pipeline / evCost).toFixed(1) + "x" : "\u2014";
    const vals = [evName, seg, ev.leads, ev.hot, ev.warm, ev.cold, ev.demos, ev.leads > 0 ? (ev.demos / ev.leads * 100).toFixed(1) + "%" : "\u2014", ov.meetings > 0 ? ov.meetings : "\u2014", evCost > 0 ? fmt(evCost) : "\u2014", ov.influenced_pipeline > 0 ? fmt(ov.influenced_pipeline) : "\u2014", evRoi];
    ex = m.l + 6;
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(8);
    pdf.setTextColor(...fog);
    vals.forEach((v, i) => { pdf.text(String(v), ex, y + 11); ex += evColW[i]; });
    pdf.setDrawColor(...stone);
    pdf.setLineWidth(0.3);
    pdf.line(m.l, y + 15, m.l + cw, y + 15);
    y += 16;
  });

  pdf.setDrawColor(...cyan);
  pdf.setLineWidth(0.5);
  pdf.line(m.l, ph - m.b, pw - m.r, ph - m.b);
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(7);
  pdf.setTextColor(...stone);
  pdf.text(companyName + "  |  EXIQ Exhibitor Intelligence  |  Proprietary & Confidential", m.l, ph - m.b + 12);
  pdf.text("Generated " + new Date().toLocaleString(), pw - m.r, ph - m.b + 12, { align: "right" });

  pdf.save("exiq_executive_summary_" + new Date().toISOString().slice(0, 10) + ".pdf");
}

// ── Event Brief PDF Export ────────────────────────────────────────────────────

// ── Event Brief PDF Export (Option B redesign) ────────────────────────────────

// ── Event Brief PDF — Option C: Modern Accent ────────────────────────────────
export async function generateEventBriefPDF({ brief, debrief, calEv, eventName, companyName = "EXIQ", actuals = {} }) {
  await loadScript("https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js");
  const { jsPDF } = window.jspdf;
  const pdf = new jsPDF({ orientation: "portrait", unit: "pt", format: "letter" });

  // ── Palette ───────────────────────────────────────────────────────────────
  const COL = {
    onyx:   [10,  23,  32],
    onyxMd: [15,  37,  52],
    cyan:   [34,  211, 238],
    cyanDk: [8,   137, 178],
    white:  [255, 255, 255],
    stone:  [96,  115, 128],
    stoneL: [160, 174, 182],
    surf:   [244, 246, 248],
    bord:   [228, 232, 235],
    text:   [22,  38,  52],
    cyanBg: [218, 244, 252],
  };

  // ── Geometry ──────────────────────────────────────────────────────────────
  const PW = 612, PH = 792;
  const ML = 48, MR = 48;
  const CW = PW - ML - MR;   // 516pt
  const MB = 40;
  let y = 0;

  // ── Helpers ───────────────────────────────────────────────────────────────
  const strip = t => (!t ? "" : String(t)
    .replace(/<cite[^>]*>/gi,"").replace(/<\/cite>/gi,"")
    .replace(/\*\*/g,"").replace(/\s{2,}/g," ").trim());

  const fmtMoney = n => {
    if (!n) return "—";
    if (n >= 1000000) return "$" + (n/1000000).toFixed(1) + "M";
    if (n >= 1000)    return "$" + Math.round(n/1000) + "K";
    return "$" + Math.round(n);
  };

  // CRITICAL: always set font before splitting — font metrics must match render
  const split = (txt, w, size, bold) => {
    pdf.setFont("helvetica", bold ? "bold" : "normal");
    pdf.setFontSize(size);
    return pdf.splitTextToSize(strip(txt || ""), Math.max(w - 2, 10));
  };

  const newPage = () => {
    pdf.addPage();
    // Slim continuation strip
    pdf.setFillColor(...COL.onyx);
    pdf.rect(0, 0, PW, 24, "F");
    pdf.setFillColor(...COL.cyan);
    pdf.rect(0, 24, PW, 3, "F");
    pdf.setFont("helvetica", "bold"); pdf.setFontSize(7);
    pdf.setTextColor(...COL.cyan);
    pdf.text(strip(eventName).toUpperCase() + "  ·  CONTINUED", ML, 16);
    y = 40;
  };

  const pageBreak = () => newPage();
  const need = h => { if (y + h > PH - MB) newPage(); };

  const divLine = (gap = 14) => {
    need(gap + 1);
    pdf.setDrawColor(...COL.bord); pdf.setLineWidth(0.4);
    pdf.line(ML, y, PW - MR, y);
    y += gap;
  };

  // Arrow-shaped section header (Option C style)
  const secBar = (label, source) => {
    need(26);
    // Dark filled arrow shape
    pdf.setFillColor(...COL.onyx);
    pdf.rect(ML, y, CW, 20, "F");
    // Cyan left accent bar
    pdf.setFillColor(...COL.cyan);
    pdf.rect(ML, y, 4, 20, "F");
    // Label
    pdf.setFont("helvetica", "bold"); pdf.setFontSize(8);
    pdf.setTextColor(...COL.white);
    pdf.text(label.toUpperCase(), ML + 12, y + 13.5);
    // Source tag
    if (source) {
      pdf.setFont("helvetica", "normal"); pdf.setFontSize(7);
      pdf.setTextColor(...COL.stoneL);
      pdf.text(source, PW - MR, y + 13.5, { align: "right" });
    }
    y += 24;
  };

  // Field label + body — font set before split
  const block = (label, value, gap) => {
    const s = strip(value || "");
    if (!s) return;
    const lines = split(s, CW, 9.5, false);
    const h = 12 + lines.length * 12.5 + (gap ?? 12);
    need(h);
    const sy = y;
    pdf.setFont("helvetica", "bold"); pdf.setFontSize(7.5);
    pdf.setTextColor(...COL.cyanDk);     // Blue labels — Option C
    pdf.text(label.toUpperCase(), ML, sy);
    pdf.setFont("helvetica", "normal"); pdf.setFontSize(9.5);
    pdf.setTextColor(...COL.text);
    lines.forEach((l, i) => pdf.text(l, ML, sy + 11 + i * 12.5));
    y = sy + 11 + lines.length * 12.5 + (gap ?? 12);
  };

  // KPI boxes — cyan top border (Option C style)
  const kpis = (items, cols) => {
    const n = cols || items.length;
    need(46);
    const bw = Math.floor((CW - (n - 1) * 5) / n);
    const ky = y;
    items.forEach(({ label, value }, i) => {
      const x = ML + i * (bw + 5);
      // Cyan top border
      pdf.setFillColor(...COL.surf);
      pdf.roundedRect(x, ky, bw, 36, 3, 3, "F");
      pdf.setFillColor(...COL.cyan);
      pdf.rect(x, ky, bw, 3, "F");  // top cyan stripe
      // Label
      pdf.setFont("helvetica", "bold"); pdf.setFontSize(6.5);
      pdf.setTextColor(...COL.stone);
      pdf.text(strip(label).toUpperCase(), x + 8, ky + 13);
      // Value
      const v = strip(String(value || "—"));
      const fs = v.length > 14 ? 9.5 : v.length > 9 ? 12 : 15;
      pdf.setFont("helvetica", "bold"); pdf.setFontSize(fs);
      pdf.setTextColor(...COL.onyx);
      const vl = split(v, bw - 14, fs, true);
      pdf.text(vl[0] || v.slice(0,20), x + 8, ky + 28);
    });
    y = ky + 44;
  };

  // ── PAGE 1 HEADER ─────────────────────────────────────────────────────────
  // Dark header
  pdf.setFillColor(...COL.onyx);
  pdf.rect(0, 0, PW, 78, "F");
  // Subtle right accent panel
  pdf.setFillColor(255, 255, 255);
  pdf.setGState(pdf.GState({ opacity: 0.04 }));
  pdf.rect(PW - 130, 0, 130, 78, "F");
  pdf.setGState(pdf.GState({ opacity: 1.0 }));
  // Cyan + mid gradient stripe at bottom of header
  pdf.setFillColor(...COL.cyan);
  pdf.rect(0, 74, PW * 0.6, 4, "F");
  pdf.setFillColor(...COL.cyanDk);
  pdf.rect(PW * 0.6, 74, PW * 0.4, 4, "F");

  // Spark dot + label
  pdf.setFillColor(...COL.cyan);
  pdf.circle(ML + 4, 18, 4, "F");
  pdf.setFont("helvetica", "bold"); pdf.setFontSize(7.5);
  pdf.setTextColor(...COL.cyan);
  pdf.text("EXIQ INTELLIGENCE  ·  EVENT BRIEF", ML + 12, 21);

  // Event name
  const titleL = split(eventName, CW - 80, 20, true);
  pdf.setFont("helvetica", "bold"); pdf.setFontSize(20);
  pdf.setTextColor(...COL.white);
  pdf.text(titleL[0], ML, 48);

  // Meta
  const metaParts = [
    calEv?.start_date ? new Date(calEv.start_date+"T00:00:00").toLocaleDateString("en-US",{month:"long",day:"numeric",year:"numeric"}) : null,
    calEv?.location || null,
    calEv?.team     || null,
  ].filter(Boolean).join("  |  ");
  pdf.setFont("helvetica", "normal"); pdf.setFontSize(8.5);
  pdf.setTextColor(...COL.stoneL);
  pdf.text(split(metaParts, CW - 90, 8.5, false)[0] || metaParts, ML, 64);
  pdf.text("Generated " + new Date().toLocaleDateString(), PW - MR, 64, { align: "right" });

  y = 92;

  // ── EXEC SUMMARY ──────────────────────────────────────────────────────────
  if (brief?.ai_summary) {
    const lines = split(brief.ai_summary, CW, 10.5, false);
    need(lines.length * 13.5 + 16);
    const sy = y;
    pdf.setFont("helvetica", "normal"); pdf.setFontSize(10.5);
    pdf.setTextColor(...COL.text);
    lines.forEach((l, i) => pdf.text(l, ML, sy + i * 13.5));
    y = sy + lines.length * 13.5 + 8;
    divLine(14);
  }

  // ── EVENT OVERVIEW ────────────────────────────────────────────────────────
  secBar("Event Overview", "AI Research");
  y += 6;
  kpis([
    { label: "Expected attendees",   value: brief?.expected_attendees ? Number(brief.expected_attendees).toLocaleString() : "—" },
    { label: "Exhibiting companies", value: brief?.exhibitor_count    ? Number(brief.exhibitor_count).toLocaleString()+"+" : "—" },
    { label: "Event website",        value: brief?.event_website ? strip(brief.event_website).replace(/^https?:\/\//,"").split("/")[0] : "—" },
  ]);
  y += 8;
  block("Attendee breakdown", brief?.attendee_segments, 12);
  block("Strategic overview",  brief?.ai_narrative, 6);

  // ── PAGE BREAK → AGENDA & SPEAKERS ───────────────────────────────────────
  pageBreak();
  secBar("Agenda & Speakers", "AI Research");
  y += 6;
  block("Key sessions & tracks", brief?.agenda_highlights, 16);
  block("Confirmed speakers",    brief?.key_speakers, 6);

  // ── PAGE BREAK → OUR PARTICIPATION ───────────────────────────────────────
  pageBreak();
  secBar("Our Participation", "Dashboard");
  y += 6;
  kpis([
    { label: "Target leads",    value: calEv?.goal_leads    ? Number(calEv.goal_leads).toLocaleString()    : "—" },
    { label: "Target pipeline", value: calEv?.goal_pipeline ? fmtMoney(calEv.goal_pipeline) : "—" },
    { label: "Target meetings", value: calEv?.goal_meetings ? Number(calEv.goal_meetings).toLocaleString() : "—" },
    { label: "Budget",          value: calEv?.planned_budget ? fmtMoney(calEv.planned_budget) : "—" },
  ]);
  y += 8;
  const boothStr = [
    (brief?.booth_number || calEv?.booth_number) ? "Booth #"+(brief?.booth_number||calEv?.booth_number) : null,
    brief?.booth_size || calEv?.booth_size || null,
    calEv?.location || null,
  ].filter(Boolean).join("  ·  ");
  block("Booth", boothStr, 12);
  block("Strategic plan", brief?.ai_account_prep, 6);

  // ── STAFFING ──────────────────────────────────────────────────────────────
  let staffData = [];
  try {
    const raw = brief?.staff_assigned;
    if (raw && (raw.trim().startsWith("[") || raw.trim().startsWith("{"))) {
      const p = JSON.parse(raw); if (Array.isArray(p)) staffData = p;
    }
  } catch {}
  if (!staffData.length) {
    try {
      const raw = brief?.staff_schedule;
      if (raw) { const p = typeof raw === "string" ? JSON.parse(raw) : raw; if (Array.isArray(p)) staffData = p; }
    } catch {}
  }

  if (staffData.length > 0) {
    divLine();
    secBar("Staffing", "Staff Scheduling");
    y += 4;

    const allDates = [...new Set(staffData.flatMap(p=>(p.shifts||[]).map(s=>s.date)).filter(Boolean).sort())];

    if (allDates.length > 0) {
      const nameW = 140, dayW = Math.floor((CW - nameW) / allDates.length), rowH = 30, headH = 26;
      need(headH + staffData.length * rowH + 8);
      const tY = y;

      // Header row
      pdf.setFillColor(...COL.onyxMd);
      pdf.rect(ML, tY, CW, headH, "F");
      pdf.setFillColor(...COL.cyan);
      pdf.rect(ML, tY, 4, headH, "F");
      pdf.setFont("helvetica", "bold"); pdf.setFontSize(7);
      pdf.setTextColor(...COL.stoneL);
      pdf.text("STAFF MEMBER", ML + 12, tY + 16);
      allDates.forEach((d, i) => {
        const dt = new Date(d+"T00:00:00");
        const cx = ML + nameW + i * dayW + dayW/2;
        pdf.setFont("helvetica","bold"); pdf.setFontSize(7); pdf.setTextColor(...COL.stoneL);
        pdf.text(dt.toLocaleDateString("en-US",{weekday:"short"}).toUpperCase(), cx, tY+11, {align:"center"});
        pdf.setFont("helvetica","normal"); pdf.setFontSize(6.5); pdf.setTextColor(...COL.stone);
        pdf.text(dt.toLocaleDateString("en-US",{month:"short",day:"numeric"}), cx, tY+20, {align:"center"});
      });
      y = tY + headH;

      staffData.forEach((person, pi) => {
        const rY = y;
        pdf.setFillColor(pi%2===0 ? 249 : 255, pi%2===0 ? 251 : 255, pi%2===0 ? 253 : 255);
        pdf.rect(ML, rY, CW, rowH, "F");
        // Cyan left accent on row
        pdf.setFillColor(...COL.cyan);
        pdf.rect(ML, rY, 4, rowH, "F");
        // Avatar
        pdf.setFillColor(...COL.cyanBg);
        pdf.circle(ML + 18, rY + rowH/2, 10, "F");
        const init = (person.name||"?").split(" ").map(w=>w[0]||"").join("").toUpperCase().slice(0,2);
        pdf.setFont("helvetica","bold"); pdf.setFontSize(7); pdf.setTextColor(...COL.cyanDk);
        pdf.text(init, ML+18, rY+rowH/2+2.5, {align:"center"});
        // Name + role
        pdf.setFont("helvetica","bold"); pdf.setFontSize(9); pdf.setTextColor(...COL.text);
        pdf.text(split(person.name||"", nameW-36, 9, true)[0], ML+32, rY+11);
        pdf.setFont("helvetica","normal"); pdf.setFontSize(7.5); pdf.setTextColor(...COL.stone);
        pdf.text(person.role||"Staff", ML+32, rY+21);
        // Day cells
        allDates.forEach((d, i) => {
          const sh = (person.shifts||[]).find(s=>s.date===d);
          const cx = ML + nameW + i * dayW;
          if (sh) {
            const isLead = (person.role||"").toLowerCase().includes("lead");
            pdf.setFillColor(...(isLead ? [200,240,252] : [224,248,254]));
            pdf.roundedRect(cx+3, rY+5, dayW-6, rowH-10, 3, 3, "F");
            // Cyan top on cell
            pdf.setFillColor(...(isLead ? COL.cyan : [100,210,235]));
            pdf.rect(cx+3, rY+5, dayW-6, 2, "F");
            const t1=(sh.start_time||"").replace(":00 ","").replace(" ","");
            const t2=(sh.end_time||"").replace(":00 ","").replace(" ","");
            const ts = t1&&t2 ? t1+"–"+t2 : t1||"On duty";
            pdf.setFont("helvetica","bold"); pdf.setFontSize(6.5); pdf.setTextColor(...COL.cyanDk);
            pdf.text(split(ts,dayW-8,6.5,true)[0], cx+dayW/2, rY+14, {align:"center"});
            if (sh.zone) {
              pdf.setFont("helvetica","normal"); pdf.setFontSize(5.5); pdf.setTextColor(...COL.stone);
              pdf.text(sh.zone.slice(0,12), cx+dayW/2, rY+22, {align:"center"});
            }
          } else {
            pdf.setFont("helvetica","normal"); pdf.setFontSize(10); pdf.setTextColor(...COL.bord);
            pdf.text("—", cx+dayW/2, rY+rowH/2+3, {align:"center"});
          }
        });
        pdf.setDrawColor(...COL.bord); pdf.setLineWidth(0.3);
        pdf.line(ML, rY+rowH, PW-MR, rY+rowH);
        y = rY + rowH;
      });
      y += 8;
    } else {
      // Simple list fallback
      staffData.forEach((p, i) => {
        need(26);
        const rY = y;
        const init = (p.name||"?").split(" ").map(w=>w[0]||"").join("").toUpperCase().slice(0,2);
        pdf.setFillColor(...COL.cyanBg);
        pdf.circle(ML+10, rY+10, 8, "F");
        pdf.setFont("helvetica","bold"); pdf.setFontSize(6.5); pdf.setTextColor(...COL.cyanDk);
        pdf.text(init, ML+10, rY+13, {align:"center"});
        pdf.setFont("helvetica","bold"); pdf.setFontSize(9); pdf.setTextColor(...COL.text);
        pdf.text(split(p.name||"",CW-28,9,true)[0], ML+22, rY+10);
        pdf.setFont("helvetica","normal"); pdf.setFontSize(7.5); pdf.setTextColor(...COL.stone);
        pdf.text(p.role||"Staff", ML+22, rY+20);
        if (i<staffData.length-1){pdf.setDrawColor(...COL.bord);pdf.setLineWidth(0.3);pdf.line(ML,rY+24,PW-MR,rY+24);}
        y = rY + 28;
      });
      y += 4;
    }
  }

  // ── MEETING SCHEDULE ──────────────────────────────────────────────────────
  let meetings = [];
  try {
    const raw = brief?.meeting_details;
    meetings = typeof raw === "string" ? JSON.parse(raw) : (Array.isArray(raw) ? raw : []);
  } catch {}

  if (meetings.length > 0) {
    divLine();
    const confCount = meetings.filter(m=>m.status==="confirmed"||m.status==="completed").length;
    secBar("Meeting Schedule", `Meeting Planner  ·  ${meetings.length} meetings  ·  ${confCount} confirmed`);
    y += 4;
    meetings.forEach((m, i) => {
      need(38);
      const mY = y;
      const isConf = m.status==="confirmed"||m.status==="completed";
      const nameW = CW - 82 - 64;
      // Date + time
      if (m.date) {
        const d = new Date(m.date+"T00:00:00");
        pdf.setFont("helvetica","bold"); pdf.setFontSize(7.5); pdf.setTextColor(...COL.stone);
        pdf.text(d.toLocaleDateString("en-US",{weekday:"short",month:"short",day:"numeric"}), ML, mY+9);
      }
      pdf.setFont("helvetica","bold"); pdf.setFontSize(9.5); pdf.setTextColor(...COL.onyx);
      pdf.text(m.time||"TBD", ML, mY+21);
      // Contact + detail
      const ns = split([m.contact,m.company].filter(Boolean).join("  ·  "), nameW, 9.5, true)[0];
      pdf.setFont("helvetica","bold"); pdf.setFontSize(9.5); pdf.setTextColor(...COL.text);
      pdf.text(ns, ML+82, mY+9);
      const det = [m.type,m.location,m.rep].filter(Boolean).join("  ·  ");
      pdf.setFont("helvetica","normal"); pdf.setFontSize(8); pdf.setTextColor(...COL.stone);
      pdf.text(split(det,nameW,8,false)[0], ML+82, mY+21);
      // Status badge
      const bW = 56;
      pdf.setFillColor(...(isConf ? [210,247,252] : [255,243,218]));
      pdf.roundedRect(PW-MR-bW, mY+5, bW, 14, 3, 3, "F");
      pdf.setFillColor(...(isConf ? COL.cyan : [242,171,22]));
      pdf.rect(PW-MR-bW, mY+5, bW, 2, "F");
      pdf.setFont("helvetica","bold"); pdf.setFontSize(7);
      pdf.setTextColor(...(isConf ? COL.cyanDk : [140,95,10]));
      const bl = isConf?"Confirmed":m.status==="no_show"?"No-show":"Requested";
      pdf.text(bl, PW-MR-bW/2, mY+15, {align:"center"});
      if (i<meetings.length-1){pdf.setDrawColor(...COL.bord);pdf.setLineWidth(0.3);pdf.line(ML,mY+34,PW-MR,mY+34);}
      y = mY + 36;
    });
    y += 8;
  }

  // ── PAGE BREAK → COMPETITIVE + WATCH ITEMS ───────────────────────────────
  if (brief?.ai_competitive_watchlist || (brief?.ai_talking_points||"").split("\n").filter(Boolean).length>0) {
    pageBreak();
  }

  if (brief?.ai_competitive_watchlist) {
    secBar("Competitive Landscape", "AI Research");
    y += 6;
    block("Analysis", brief.ai_competitive_watchlist, 8);
    divLine();
  }

  const watchItems = (brief?.ai_talking_points||"").split("\n").filter(Boolean);
  if (watchItems.length > 0) {
    secBar("Key Watch Items", "AI");
    y += 6;
    watchItems.forEach((item, i) => {
      const s = strip(item.replace(/^-\s*/,""));
      const lines = split(s, CW-30, 9.5, false);
      const h = lines.length * 12.5 + 18;
      need(h);
      const wY = y;
      // Number circle with cyan fill
      pdf.setFillColor(...COL.cyan);
      pdf.circle(ML+9, wY+10, 9, "F");
      pdf.setFont("helvetica","bold"); pdf.setFontSize(8); pdf.setTextColor(...COL.onyx);
      pdf.text(String(i+1), ML+9, wY+13.5, {align:"center"});
      pdf.setFont("helvetica","normal"); pdf.setFontSize(9.5); pdf.setTextColor(...COL.text);
      lines.forEach((l,li) => pdf.text(l, ML+24, wY+10+li*12.5));
      if (i<watchItems.length-1){pdf.setDrawColor(...COL.bord);pdf.setLineWidth(0.3);pdf.line(ML,wY+h-4,PW-MR,wY+h-4);}
      y = wY + h;
    });
  }

  // ── FOOTERS ───────────────────────────────────────────────────────────────
  const total = pdf.getNumberOfPages();
  for (let p = 1; p <= total; p++) {
    pdf.setPage(p);
    pdf.setFillColor(...COL.onyx);
    pdf.rect(0, PH-28, PW, 28, "F");
    pdf.setFillColor(...COL.cyan);
    pdf.rect(0, PH-28, PW*0.5, 2, "F");
    pdf.setFillColor(...COL.cyanDk);
    pdf.rect(PW*0.5, PH-28, PW*0.5, 2, "F");
    pdf.setFont("helvetica","normal"); pdf.setFontSize(7);
    pdf.setTextColor(...COL.stone);
    pdf.text(`${companyName}  |  EXIQ Intelligence  |  Confidential`, ML, PH-10);
    // Page number badge
    pdf.setFillColor(...COL.cyan);
    pdf.roundedRect(PW-MR-36, PH-22, 36, 14, 3, 3, "F");
    pdf.setFont("helvetica","bold"); pdf.setFontSize(7.5);
    pdf.setTextColor(...COL.onyx);
    pdf.text(`${p} of ${total}`, PW-MR-18, PH-11, {align:"center"});
  }

  const safe = eventName.replace(/[^a-z0-9]/gi,"_").toLowerCase();
  pdf.save(`exiq_brief_${safe}_${new Date().toISOString().slice(0,10)}.pdf`);
}

// ── Debrief PDF Export ────────────────────────────────────────────────────────
export async function generateDebriefPDF({ debrief, ev, actuals = {}, companyName = "EXIQ" }) {
  await loadScript("https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js");
  const { jsPDF } = window.jspdf;
  const pdf = new jsPDF({ orientation: "portrait", unit: "pt", format: "letter" });

  const PW = 612, PH = 792, ML = 48, MR = 48, MB = 40;
  const CW = PW - ML - MR;
  let y = 0;

  const COL = {
    onyx:   [10,  23,  32],
    onyxMd: [15,  37,  52],
    cyan:   [34,  211, 238],
    cyanDk: [8,   137, 178],
    white:  [255, 255, 255],
    stone:  [96,  115, 128],
    stoneL: [160, 174, 182],
    surf:   [244, 246, 248],
    bord:   [228, 232, 235],
    text:   [22,  38,  52],
    green:  [34,  197, 94],
    red:    [239, 68,  68],
    gold:   [234, 179, 8],
  };

  const strip = t => (!t ? "" : String(t)
    .replace(/<cite[^>]*>/gi,"").replace(/<\/cite>/gi,"")
    .replace(/\*\*/g,"").replace(/\s{2,}/g," ").trim());

  const splitTxt = (txt, w, size, bold) => {
    pdf.setFont("helvetica", bold ? "bold" : "normal");
    pdf.setFontSize(size);
    return pdf.splitTextToSize(strip(txt || ""), Math.max(w - 2, 10));
  };

  const fmtMoney = n => {
    if (!n) return "—";
    if (n >= 1000000) return "$" + (n/1000000).toFixed(1) + "M";
    if (n >= 1000)    return "$" + Math.round(n/1000) + "K";
    return "$" + Math.round(n);
  };

  const newPage = () => {
    pdf.addPage();
    pdf.setFillColor(...COL.onyx);
    pdf.rect(0, 0, PW, 24, "F");
    pdf.setFillColor(...COL.cyan);
    pdf.rect(0, 24, PW, 3, "F");
    pdf.setFont("helvetica", "bold"); pdf.setFontSize(7);
    pdf.setTextColor(...COL.cyan);
    pdf.text(strip(ev?.name || "").toUpperCase() + "  ·  POST-EVENT DEBRIEF", ML, 16);
    y = 44;
  };

  const need = h => { if (y + h > PH - MB) newPage(); };

  const secBar = (label, color) => {
    need(28);
    const c = color || COL.cyan;
    pdf.setFillColor(...COL.onyxMd);
    pdf.rect(ML, y, CW, 22, "F");
    pdf.setFillColor(...c);
    pdf.rect(ML, y, 4, 22, "F");
    pdf.setFont("helvetica", "bold"); pdf.setFontSize(8.5);
    pdf.setTextColor(...COL.white);
    pdf.text(label.toUpperCase(), ML + 14, y + 15);
    y += 28;
  };

  const bullet = (text, i, isNumbered, color) => {
    const s = strip(text.replace(/^[-•*\d.]+\s*/, ""));
    if (!s) return;
    const lines = splitTxt(s, CW - 26, 9.5, false);
    const h = lines.length * 13 + 10;
    need(h);
    const bY = y;
    if (isNumbered) {
      pdf.setFillColor(...(color || COL.cyan));
      pdf.circle(ML + 9, bY + 9, 8, "F");
      pdf.setFont("helvetica", "bold"); pdf.setFontSize(7.5);
      pdf.setTextColor(...COL.onyx);
      pdf.text(String(i + 1), ML + 9, bY + 12, { align: "center" });
    } else {
      pdf.setFillColor(...(color || COL.cyan));
      pdf.circle(ML + 6, bY + 8, 3, "F");
    }
    const tx = isNumbered ? ML + 22 : ML + 16;
    pdf.setFont("helvetica", "normal"); pdf.setFontSize(9.5);
    pdf.setTextColor(...COL.text);
    lines.forEach((l, li) => pdf.text(l, tx, bY + 10 + li * 13));
    y = bY + h;
  };

  // Parse debrief markdown into sections
  function parseDebrief(text) {
    const sections = {};
    const sectionOrder = [
      "Overall Assessment",
      "What Worked",
      "What Fell Short",
      "Recommendations for Next Year",
      "Leadership Talking Points",
    ];
    let current = null;
    let buf = [];
    const lines = (text || "").split("\n");
    for (const line of lines) {
      const trimmed = line.trim();
      const matched = sectionOrder.find(s =>
        trimmed.replace(/\*\*/g, "").toLowerCase().includes(s.toLowerCase())
      );
      if (matched) {
        if (current && buf.length) sections[current] = buf.join("\n").trim();
        current = matched;
        buf = [];
      } else if (current) {
        buf.push(line);
      }
    }
    if (current && buf.length) sections[current] = buf.join("\n").trim();
    return sections;
  }

  // ── PAGE 1: HEADER ────────────────────────────────────────────────────────
  pdf.setFillColor(...COL.onyx);
  pdf.rect(0, 0, PW, 90, "F");
  pdf.setFillColor(...COL.cyan);
  pdf.rect(0, 86, PW * 0.55, 4, "F");
  pdf.setFillColor(...COL.cyanDk);
  pdf.rect(PW * 0.55, 86, PW * 0.45, 4, "F");

  // Badge
  pdf.setFillColor(...COL.cyan);
  pdf.circle(ML + 5, 18, 4, "F");
  pdf.setFont("helvetica", "bold"); pdf.setFontSize(7.5);
  pdf.setTextColor(...COL.cyan);
  pdf.text("EXIQ INTELLIGENCE  ·  POST-EVENT DEBRIEF", ML + 14, 21);

  // Event name
  const titleLines = splitTxt(ev?.name || "Event Debrief", CW - 60, 22, true);
  pdf.setFont("helvetica", "bold"); pdf.setFontSize(22);
  pdf.setTextColor(...COL.white);
  pdf.text(titleLines[0], ML, 52);

  // Meta
  const meta = [
    ev?.start_date ? new Date(ev.start_date + "T00:00:00").toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }) : null,
    ev?.location || null,
    ev?.team || null,
  ].filter(Boolean).join("  |  ");
  pdf.setFont("helvetica", "normal"); pdf.setFontSize(8.5);
  pdf.setTextColor(...COL.stoneL);
  if (meta) pdf.text(splitTxt(meta, CW, 8.5, false)[0], ML, 68);
  pdf.text("Generated " + new Date().toLocaleDateString(), PW - MR, 68, { align: "right" });

  y = 104;

  // ── ACTUALS SUMMARY ROW ───────────────────────────────────────────────────
  const kpiItems = [
    { label: "Leads Captured",  value: actuals.leads    ? String(actuals.leads)               : "—" },
    { label: "Meetings Held",   value: actuals.meetings ? String(actuals.meetings)             : "—" },
    { label: "Pipeline",        value: actuals.pipeline ? fmtMoney(actuals.pipeline)           : "—" },
    { label: "Total Spend",     value: actuals.spend    ? fmtMoney(actuals.spend)              : "—" },
    { label: "Cost / Lead",     value: actuals.costPerLead ? fmtMoney(actuals.costPerLead)     : "—" },
  ];
  const kpiW = Math.floor((CW - (kpiItems.length - 1) * 6) / kpiItems.length);
  kpiItems.forEach(({ label, value }, i) => {
    const x = ML + i * (kpiW + 6);
    pdf.setFillColor(...COL.surf);
    pdf.roundedRect(x, y, kpiW, 40, 3, 3, "F");
    pdf.setFillColor(...COL.cyan);
    pdf.rect(x, y, kpiW, 3, "F");
    pdf.setFont("helvetica", "bold"); pdf.setFontSize(6.5);
    pdf.setTextColor(...COL.stone);
    pdf.text(label.toUpperCase(), x + kpiW / 2, y + 14, { align: "center" });
    pdf.setFont("helvetica", "bold"); pdf.setFontSize(14);
    pdf.setTextColor(...COL.onyx);
    pdf.text(String(value), x + kpiW / 2, y + 32, { align: "center" });
  });
  y += 52;

  // ── PARSE SECTIONS ────────────────────────────────────────────────────────
  const sections = parseDebrief(debrief);

  // ── OVERALL ASSESSMENT ────────────────────────────────────────────────────
  const assessment = sections["Overall Assessment"] || "";
  if (assessment) {
    secBar("Overall Assessment");
    const grade = assessment.match(/Grade:\s*([A-F][+-]?)/i)?.[1] || null;
    const narrative = assessment.replace(/Grade:\s*[A-F][+-]?\s*[-–—]?\s*/i, "").replace(/---/g, "").trim();

    if (grade) {
      need(48);
      // Grade box
      const gradeColor = grade.startsWith("A") ? COL.green : grade.startsWith("B") ? COL.cyan : grade.startsWith("C") ? COL.gold : COL.red;
      pdf.setFillColor(...gradeColor);
      pdf.roundedRect(ML, y, 44, 44, 4, 4, "F");
      pdf.setFont("helvetica", "bold"); pdf.setFontSize(24);
      pdf.setTextColor(...COL.white);
      pdf.text(grade, ML + 22, y + 30, { align: "center" });
      // Narrative beside grade
      const nLines = splitTxt(narrative, CW - 56, 9.5, false);
      pdf.setFont("helvetica", "normal"); pdf.setFontSize(9.5);
      pdf.setTextColor(...COL.text);
      nLines.slice(0, 4).forEach((l, i) => pdf.text(l, ML + 54, y + 13 + i * 13));
      y += Math.max(52, nLines.slice(0,4).length * 13 + 16);
      if (nLines.length > 4) {
        const rest = splitTxt(nLines.slice(4).join(" "), CW, 9.5, false);
        rest.forEach(l => { need(14); pdf.setFont("helvetica","normal"); pdf.setFontSize(9.5); pdf.setTextColor(...COL.text); pdf.text(l, ML, y); y += 13; });
        y += 6;
      }
    } else {
      const nLines = splitTxt(narrative, CW, 9.5, false);
      need(nLines.length * 13 + 12);
      pdf.setFont("helvetica", "normal"); pdf.setFontSize(9.5);
      pdf.setTextColor(...COL.text);
      nLines.forEach(l => { pdf.text(l, ML, y); y += 13; });
      y += 8;
    }
  }

  // ── WHAT WORKED ───────────────────────────────────────────────────────────
  const worked = (sections["What Worked"] || "").split("\n").filter(l => l.trim() && !l.trim().startsWith("**What"));
  if (worked.length) {
    need(32);
    secBar("What Worked", COL.green);
    worked.forEach((item, i) => bullet(item, i, false, COL.green));
    y += 8;
  }

  // ── WHAT FELL SHORT ───────────────────────────────────────────────────────
  const fell = (sections["What Fell Short"] || "").split("\n").filter(l => l.trim() && !l.trim().startsWith("**What"));
  if (fell.length) {
    need(32);
    secBar("What Fell Short", COL.red);
    fell.forEach((item, i) => bullet(item, i, false, COL.red));
    y += 8;
  }

  // ── PAGE BREAK → RECOMMENDATIONS ─────────────────────────────────────────
  newPage();
  const recs = (sections["Recommendations for Next Year"] || "").split("\n").filter(l => l.trim() && !l.trim().startsWith("**Rec"));
  if (recs.length) {
    secBar("Recommendations for Next Year", COL.cyan);
    recs.forEach((item, i) => bullet(item, i, true, COL.cyan));
    y += 12;
  }

  // ── LEADERSHIP TALKING POINTS ─────────────────────────────────────────────
  const talking = (sections["Leadership Talking Points"] || "").split("\n").filter(l => l.trim() && !l.trim().startsWith("**Lead"));
  if (talking.length) {
    need(32);
    secBar("Leadership Talking Points", COL.cyanDk);
    y += 4;
    // Talking points as styled callout boxes
    talking.forEach((item, i) => {
      const s = strip(item.replace(/^[-•*\d.]+\s*/, ""));
      if (!s) return;
      const lines = splitTxt(s, CW - 24, 9.5, false);
      const h = lines.length * 13 + 20;
      need(h + 8);
      pdf.setFillColor(...COL.surf);
      pdf.roundedRect(ML, y, CW, h, 3, 3, "F");
      pdf.setFillColor(...COL.cyanDk);
      pdf.rect(ML, y, 4, h, "F");
      pdf.setFont("helvetica", "normal"); pdf.setFontSize(9.5);
      pdf.setTextColor(...COL.text);
      lines.forEach((l, li) => pdf.text(l, ML + 14, y + 13 + li * 13));
      y += h + 8;
    });
  }

  // ── FOOTERS ───────────────────────────────────────────────────────────────
  const totalPages = pdf.getNumberOfPages();
  for (let p = 1; p <= totalPages; p++) {
    pdf.setPage(p);
    pdf.setFillColor(...COL.onyx);
    pdf.rect(0, PH - 28, PW, 28, "F");
    pdf.setFillColor(...COL.cyan);
    pdf.rect(0, PH - 28, PW * 0.5, 2, "F");
    pdf.setFillColor(...COL.cyanDk);
    pdf.rect(PW * 0.5, PH - 28, PW * 0.5, 2, "F");
    pdf.setFont("helvetica", "normal"); pdf.setFontSize(7);
    pdf.setTextColor(...COL.stone);
    pdf.text(`${companyName}  |  EXIQ Intelligence  |  Confidential`, ML, PH - 10);
    pdf.setFillColor(...COL.cyan);
    pdf.roundedRect(PW - MR - 36, PH - 22, 36, 14, 3, 3, "F");
    pdf.setFont("helvetica", "bold"); pdf.setFontSize(7.5);
    pdf.setTextColor(...COL.onyx);
    pdf.text(`${p} of ${totalPages}`, PW - MR - 18, PH - 11, { align: "center" });
  }

  const safeName = (ev?.name || "debrief").replace(/[^a-z0-9]/gi, "_").toLowerCase();
  pdf.save(`exiq_debrief_${safeName}_${new Date().toISOString().slice(0, 10)}.pdf`);
}
